import { Bar, BarConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import {
  GET_IDEA_COUNT_BY_BUSINESS_AREAS,
  IdeaCountByBusinessAreas,
} from '@frontend/data-access';
import { Card } from 'antd';
import { useEffect, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

function IdeaCountsByChannel({
  channelId,
  className,
  challengeId,
  innovationSpaceId,
}: {
  channelId?: string;
  className?: string;
  challengeId?: string;
  innovationSpaceId?: string;
}) {
  const { t } = useTranslation('common');

  const [getIdeaCountByBusinessAreas, { data, loading }] = useLazyQuery<{
    channelIdeaCountByBusinessAreas: IdeaCountByBusinessAreas[];
  }>(GET_IDEA_COUNT_BY_BUSINESS_AREAS);

  useEffect(() => {
    if (!channelId) return;
    getIdeaCountByBusinessAreas({
      variables: { channelId, challengeId, spaceId: innovationSpaceId },
    });
  }, [channelId, challengeId, innovationSpaceId, getIdeaCountByBusinessAreas]);

  const chartData = useMemo(() => {
    const realData = data?.channelIdeaCountByBusinessAreas || [];
    if (realData.length > 0) {
      return realData;
    }
    // Return placeholder data with 0 count when no data is available
    return [{ businessArea: t('no_data'), count: 0 }];
  }, [data, t]);

  const config: BarConfig = useMemo(
    () => ({
      data: chartData,
      loading,
      xField: 'businessArea',
      yField: 'count',
      legend: false,
      color: '#529FF0',
      marginRight: 20,
      label: {
        text: 'count',
        position: 'right',
        textAlign: 'start',
        dx: 8,
      },
      // Ensure y-axis starts from 0 and includes at least value 1
      yAxis: {
        min: 0,
        max: chartData.some((item) => item.count > 0) ? undefined : 1,
      },
    }),
    [chartData, loading]
  );

  return (
    <div className={className}>
      <Card style={{ borderRadius: 20 }}>
        <div className="space-y-4">
          <div className="text-sm text-gray-900 font-medium">
            {t('idea_counts_by_business_functional_area')}
          </div>
          <Bar {...config} />
        </div>
      </Card>
    </div>
  );
}

export default IdeaCountsByChannel;
